import React from 'react';
import { Card, Group, Stack, Text, Badge, ActionIcon, Tooltip } from '@mantine/core';
import { useUpcomingAssignedEvents } from '../../hooks/useUpcomingAssignedEvents';
import { useNavigate } from 'react-router-dom';
import { IconCalendarPlus } from '@tabler/icons-react';
import { buildIcsEvent } from '../../utils/ics';

function fmtTimeRange(start, end) {
  const s = new Date(start);
  const e = new Date(end || start);
  const tf = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${tf(s)} - ${tf(e)}`;
}

export default function UpcomingEvents({ user, plannings }) {
  const navigate = useNavigate();
  const { loading, groups, conflictIds } = useUpcomingAssignedEvents(user, plannings);

  const addToCalendar = (ev) => {
    try {
      const ics = buildIcsEvent({
        title: ev.title,
        start: ev.start,
        end: ev.end,
        description: `From ${ev.planningName}`,
        uid: `${ev.planningId}-${ev.id}`,
        url: window.location.origin + `/planning/${ev.planningId}`,
      });
      const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ymd = new Date(ev.start).toISOString().slice(0, 10);
      a.href = url;
      a.download = `${ymd}-${(ev.title || 'event').replace(/\s+/g, '-').slice(0, 50)}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to build ICS', e);
    }
  };

  if (!user) return null;
  return (
    <Stack mt="lg" gap="sm">
      <Group justify="space-between" align="center">
        <Text fw={600}>My Upcoming Events</Text>
        {loading && <Badge color="gray">Loading…</Badge>}
      </Group>
      {groups.length === 0 && !loading && (
        <Text c="dimmed" size="sm">No upcoming assignments.</Text>
      )}
      {groups.map((g) => (
        <Stack key={g.dateKey} gap={6}>
          <Text fw={600}>{new Date(g.events[0].start).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</Text>
          {g.events.map((ev) => (
            <Card
              key={`${ev.planningId}_${ev.id}`}
              withBorder
              shadow="sm"
              radius="md"
              p="sm"
              onClick={() => navigate(`/planning/${ev.planningId}`)}
              style={{ cursor: 'pointer', backgroundColor: conflictIds.has(ev.id) ? '#ffe3e3' : undefined }}
            >
              <Group justify="space-between" align="center">
                <Stack gap={0}>
                  <Text fw={600}>{ev.title}</Text>
                  <Text size="sm" c="dimmed">{ev.planningName}</Text>
                </Stack>
                <Group gap="xs" align="center">
                  <Text fw={500}>{fmtTimeRange(ev.start, ev.end)}</Text>
                  <Tooltip label="Add to my calendar">
                    <ActionIcon variant="light" onClick={(e) => { e.stopPropagation(); addToCalendar(ev); }}>
                      <IconCalendarPlus size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
            </Card>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}
